import { useState, useEffect, useCallback, useRef } from "react";
import { getToken } from "../../redux/api";
import { VOICE_BASE, BASE_AMPS, micErrorKind, parseChatSessionId } from "./voiceConfig";
import {
  appendAssistantChunk,
  buildClientReady,
  normCaption,
  parseRtviMessage,
  sameUtterance,
} from "./voiceRtvi";
import type { ErrorKind, TranscriptEntry, VoiceState } from "./voiceTypes";

let transcriptIdCounter = 0;

export function useVoiceSession(session?: string) {
  const [voiceState, setVoiceState] = useState<VoiceState>("connecting");
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef(0);
  const speakTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);

  const interimIdRef = useRef(0);
  const lastUserNormRef = useRef("");
  const botTurnIdRef = useRef(0);
  const botActiveRef = useRef(false);
  const assistantBufferRef = useRef("");
  const usesSpokenProgressRef = useRef(false);
  const connectGenRef = useRef(0);
  const clientReadySentRef = useRef(false);
  const captionHandlerRef = useRef<(raw: unknown) => void>(() => {});

  const resetCaptionState = useCallback(() => {
    interimIdRef.current = 0;
    lastUserNormRef.current = "";
    botTurnIdRef.current = 0;
    botActiveRef.current = false;
    assistantBufferRef.current = "";
    usesSpokenProgressRef.current = false;
    clientReadySentRef.current = false;
  }, []);

  const patchBotEntry = useCallback(
    (patch: Partial<Pick<TranscriptEntry, "text" | "speaking">>) => {
      const turnId = botTurnIdRef.current;
      if (!turnId) return;
      setTranscript((prev) =>
        prev.map((e) => (e.id === turnId ? { ...e, ...patch } : e)),
      );
    },
    [],
  );

  const completeAssistantTurn = useCallback(() => {
    const turnId = botTurnIdRef.current;
    if (!turnId) return;
    botActiveRef.current = false;
    botTurnIdRef.current = 0;
    assistantBufferRef.current = "";
    usesSpokenProgressRef.current = false;
    patchBotEntry({ speaking: false });
  }, [patchBotEntry]);

  const ensureBotTurn = useCallback(() => {
    if (botActiveRef.current && botTurnIdRef.current) return;
    botActiveRef.current = true;
    assistantBufferRef.current = "";
    usesSpokenProgressRef.current = false;
    const newId = ++transcriptIdCounter;
    botTurnIdRef.current = newId;
    setTranscript((prev) => [
      ...prev,
      { id: newId, role: "assistant" as const, text: "", speaking: true },
    ]);
  }, []);

  const pushAssistantText = useCallback(
    (chunk: string, fromSpokenProgress: boolean) => {
      if (!chunk) return;
      if (!fromSpokenProgress && usesSpokenProgressRef.current) return;
      if (fromSpokenProgress) usesSpokenProgressRef.current = true;

      ensureBotTurn();
      const next = appendAssistantChunk(assistantBufferRef.current, chunk);
      assistantBufferRef.current = next;
      patchBotEntry({ text: next, speaking: true });
    },
    [ensureBotTurn, patchBotEntry],
  );

  const setBotSpeaking = useCallback(
    (speaking: boolean) => {
      if (speaking) {
        if (!botActiveRef.current || !botTurnIdRef.current) {
          ensureBotTurn();
        } else {
          patchBotEntry({ speaking: true });
        }
        return;
      }
      patchBotEntry({ speaking: false });
    },
    [ensureBotTurn, patchBotEntry],
  );

  const setUserInterim = useCallback((text: string) => {
    if (!text.trim()) return;
    const norm = normCaption(text);
    if (norm === lastUserNormRef.current) return;

    setTranscript((prev) => {
      const interimId = interimIdRef.current;
      if (interimId) {
        return prev.map((e) => (e.id === interimId ? { ...e, text, interim: true } : e));
      }
      const last = prev[prev.length - 1];
      if (last?.role === "user" && sameUtterance(last.text, text)) {
        interimIdRef.current = last.id;
        return prev.map((e) =>
          e.id === last.id ? { ...e, text, interim: true } : e,
        );
      }
      const newId = ++transcriptIdCounter;
      interimIdRef.current = newId;
      return [...prev, { id: newId, role: "user" as const, text, interim: true }];
    });
  }, []);

  const setUserFinal = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const norm = normCaption(trimmed);
      if (norm === lastUserNormRef.current) return;

      completeAssistantTurn();
      lastUserNormRef.current = norm;

      setTranscript((prev) => {
        let targetId = interimIdRef.current;
        if (!targetId) {
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].role !== "user") break;
            if (sameUtterance(prev[i].text, trimmed)) targetId = prev[i].id;
            break;
          }
        }
        interimIdRef.current = 0;
        if (!targetId) {
          return [...prev, { id: ++transcriptIdCounter, role: "user" as const, text: trimmed }];
        }
        return prev.map((e) =>
          e.id === targetId ? { ...e, text: trimmed, interim: false } : e,
        );
      });
    },
    [completeAssistantTurn],
  );

  const sendClientReady = useCallback((dc: RTCDataChannel) => {
    if (clientReadySentRef.current || dc.readyState !== "open") return;
    clientReadySentRef.current = true;
    dc.send(JSON.stringify(buildClientReady()));
  }, []);

  const handleCaptionMessage = useCallback(
    (raw: unknown) => {
      const action = parseRtviMessage(raw);
      switch (action.kind) {
        case "user":
          if (action.final) setUserFinal(action.text);
          else setUserInterim(action.text);
          break;
        case "bot-spoken":
          pushAssistantText(action.text, true);
          break;
        case "bot-tts":
          pushAssistantText(action.text, false);
          break;
        case "bot-start":
          setBotSpeaking(true);
          break;
        case "bot-pause":
          setBotSpeaking(false);
          break;
        case "server-ready":
          if (dataChannelRef.current) sendClientReady(dataChannelRef.current);
          break;
        default:
          break;
      }
    },
    [pushAssistantText, sendClientReady, setBotSpeaking, setUserFinal, setUserInterim],
  );

  useEffect(() => {
    captionHandlerRef.current = handleCaptionMessage;
  }, [handleCaptionMessage]);

  const wireDataChannel = useCallback(
    (dc: RTCDataChannel) => {
      if (dataChannelRef.current === dc) return;
      dataChannelRef.current = dc;
      dc.onopen = () => sendClientReady(dc);
      if (dc.readyState === "open") sendClientReady(dc);
      dc.onmessage = (evt) => {
        const raw = evt.data;
        if (typeof raw !== "string" || raw.startsWith("ping:")) return;
        try {
          captionHandlerRef.current(JSON.parse(raw));
        } catch {
          captionHandlerRef.current(raw);
        }
      };
    },
    [sendClientReady],
  );

  const cleanupResources = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    speakTimerRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    audioCtxRef.current?.close().catch(() => {});
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current.parentNode?.removeChild(remoteAudioRef.current);
    }
    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch {}
      dataChannelRef.current = null;
    }
    resetCaptionState();
    pcRef.current = null;
    localStreamRef.current = null;
    audioCtxRef.current = null;
    remoteAudioRef.current = null;
  }, [resetCaptionState]);

  const cleanup = useCallback(() => {
    connectGenRef.current += 1;
    cleanupResources();
  }, [cleanupResources]);

  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  const startAudioAnalysis = useCallback(
    (ctx: AudioContext, remoteStream: MediaStream, localStream: MediaStream, baseAmps: number[]) => {
      const remoteAnalyser = ctx.createAnalyser();
      remoteAnalyser.fftSize = 256;
      ctx.createMediaStreamSource(remoteStream).connect(remoteAnalyser);

      const localAnalyser = ctx.createAnalyser();
      localAnalyser.fftSize = 256;
      ctx.createMediaStreamSource(localStream).connect(localAnalyser);

      const remoteData = new Uint8Array(remoteAnalyser.frequencyBinCount);
      const localData = new Uint8Array(localAnalyser.frequencyBinCount);
      const step = Math.floor(localData.length / 12);

      const tick = () => {
        rafRef.current = requestAnimationFrame(tick);
        remoteAnalyser.getByteFrequencyData(remoteData);
        localAnalyser.getByteFrequencyData(localData);

        const remoteLevel = remoteData.reduce((a, b) => a + b, 0) / remoteData.length;
        const isSpeaking = remoteLevel > 8;
        const activeData = isSpeaking ? remoteData : localData;

        barsRef.current.forEach((bar, i) => {
          if (!bar) return;
          const raw = activeData[i * step] ?? 0;
          bar.style.height = `${Math.max(3, (raw / 255) * 22 + baseAmps[i] * 4)}px`;
          bar.style.opacity = raw > 4 ? "1" : "0.45";
        });

        if (isSpeaking) {
          if (speakTimerRef.current) {
            clearTimeout(speakTimerRef.current);
            speakTimerRef.current = null;
          }
          setVoiceState("speaking");
        } else if (!speakTimerRef.current) {
          speakTimerRef.current = setTimeout(() => {
            setVoiceState("listening");
            speakTimerRef.current = null;
          }, 600);
        }
      };
      tick();
    },
    [],
  );

  const connect = useCallback(async () => {
    connectGenRef.current += 1;
    const myGen = connectGenRef.current;
    cleanupResources();
    setTranscript([]);
    setVoiceState("connecting");
    setErrorKind(null);

    const stale = () => myGen !== connectGenRef.current;
    const abortConnect = (pc?: RTCPeerConnection, stream?: MediaStream, ctx?: AudioContext) => {
      pc?.close();
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close().catch(() => {});
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorKind("mic_unavailable");
      setVoiceState("error");
      return;
    }

    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;

    try {
      let localStream: MediaStream;
      try {
        localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (micErr) {
        if (stale()) return;
        setErrorKind(micErrorKind(micErr));
        setVoiceState("error");
        audioCtx.close().catch(() => {});
        return;
      }
      if (stale()) {
        abortConnect(undefined, localStream, audioCtx);
        return;
      }
      localStreamRef.current = localStream;

      if (audioCtx.state === "suspended") await audioCtx.resume();
      if (stale()) {
        abortConnect(undefined, localStream, audioCtx);
        return;
      }

      const token = getToken() ?? "";
      const sessionId = parseChatSessionId(sessionRef.current ?? "");

      const startRes = await fetch(`${VOICE_BASE}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enableDefaultIceServers: true,
          session: sessionId,
          bearer_token: token,
        }),
      });
      if (!startRes.ok) throw new Error(`Start failed: ${startRes.status}`);
      const { sessionId: backendSid, iceConfig } = await startRes.json();
      if (stale()) {
        abortConnect(undefined, localStream, audioCtx);
        return;
      }

      const pc = new RTCPeerConnection(iceConfig);
      pcRef.current = pc;
      localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));
      wireDataChannel(pc.createDataChannel("chat", { ordered: true }));

      pc.ontrack = (e) => {
        if (stale() || e.track.kind !== "audio") return;
        const remoteStream = e.streams[0] ?? new MediaStream([e.track]);

        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
        const prevAudio = remoteAudioRef.current;
        if (prevAudio?.parentNode) prevAudio.parentNode.removeChild(prevAudio);

        const audio = document.createElement("audio");
        audio.autoplay = true;
        (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
        audio.muted = false;
        audio.style.cssText = "position:fixed;width:0;height:0;opacity:0;pointer-events:none;";
        audio.srcObject = remoteStream;
        document.body.appendChild(audio);
        remoteAudioRef.current = audio;

        audioCtx.resume().then(() => audio.play()).catch(() => {});
        startAudioAnalysis(audioCtx, remoteStream, localStream, BASE_AMPS);
      };

      pc.onconnectionstatechange = () => {
        if (stale()) return;
        if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
          setErrorKind("network");
          setVoiceState("error");
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (stale()) {
        abortConnect(pc, localStream, audioCtx);
        return;
      }

      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === "complete") {
          resolve();
          return;
        }
        const check = () => {
          if (pc.iceGatheringState === "complete") {
            pc.removeEventListener("icegatheringstatechange", check);
            resolve();
          }
        };
        pc.addEventListener("icegatheringstatechange", check);
        setTimeout(resolve, 4000);
      });
      if (stale()) {
        abortConnect(pc, localStream, audioCtx);
        return;
      }

      const offerRes = await fetch(`${VOICE_BASE}/sessions/${backendSid}/api/offer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sdp: pc.localDescription?.sdp,
          type: pc.localDescription?.type,
          request_data: { session: sessionId, bearer_token: token },
          pc_id: null,
          restart_pc: null,
        }),
      });
      if (!offerRes.ok) throw new Error(`Offer failed: ${offerRes.status}`);
      const answer = await offerRes.json();
      if (stale()) {
        abortConnect(pc, localStream, audioCtx);
        return;
      }
      await pc.setRemoteDescription(new RTCSessionDescription(answer));

      if (stale()) {
        abortConnect(pc, localStream, audioCtx);
        return;
      }
      setVoiceState("listening");
    } catch (err) {
      if (stale()) return;
      console.error("[VoiceMode] Connection error:", err);
      setErrorKind((prev) => prev ?? "network");
      setVoiceState("error");
    }
  }, [cleanupResources, startAudioAnalysis, wireDataChannel]);

  useEffect(() => {
    connect();
    return cleanup;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleCaptions = transcript.filter(
    (e) => e.text || e.interim || (e.role === "assistant" && e.speaking),
  );

  return {
    voiceState,
    errorKind,
    transcript,
    visibleCaptions,
    transcriptScrollRef,
    barsRef,
    connect,
    cleanup,
  };
}
